import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

import { Banner } from './Banner';

export const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqs = [
    {
      question: "What conditions can Physical Therapy help with?",
      answer: "Physical therapy can effectively treat a wide range of conditions including: Sports injuries (ACL/MCL tears, ankle sprains, tennis elbow), Orthopedic conditions (arthritis, joint replacements, fractures), Musculoskeletal pain (back pain, neck pain, shoulder impingement), Post-surgical rehabilitation, Movement disorders, Balance issues, Chronic pain conditions, Work-related injuries, and Auto accident injuries. Each condition is treated with evidence-based techniques tailored to maximize your body's healing potential."
    },
    {
      question: "What manual therapy techniques are available at GymRatRehab?",
      answer: "GymRatRehab offers advanced manual therapy techniques including myofascial release, integrative dry needling, cupping therapy, kinesiotaping, joint mobilization, and specialized soft tissue work. These hands-on treatments help improve mobility, reduce pain, and restore normal function."
    },
    {
      question: "Where is GymRatRehab located in Gilbert, Arizona?",
      answer: "GymRatRehab operates located inside Alexander Medical Center in Gilbert, Arizona. We serve the Southeast Valley including Apache Junction, Chandler, Gilbert, Mesa, Queen Creek, and San Tan Valley with expert physical therapy services."
    },
    {
      question: "What are the office hours for GymRatRehab?",
      answer: "GymRatRehab is available by appointment only on Tuesdays and Thursdays from 2:00 PM to 5:00 PM. Contact us at Info@gymratrehab.com to schedule your consultation."
    },
    {
      question: "How experienced is Douglas Mitchell, PT, DPT?",
      answer: "Douglas Mitchell, PT, DPT is a licensed Doctor of Physical Therapy (DPT) with over 20 years of clinical experience. He has successfully treated over 1000+ patients, specializing in sports injuries and orthopedic rehabilitation with a 95% success rate."
    },
    {
      question: "What makes GymRatRehab different from other physical therapy clinics?",
      answer: "Douglas Mitchell, PT, DPT's philosophy focuses on maximizing each patient's body's healing potential through evidence-based treatment and personalized care plans. His extensive experience in manual therapy techniques like dry needling and cupping, combined with 20+ years of expertise, provides comprehensive rehabilitation solutions."
    },
    {
      question: "Do you treat ACL injuries and post-surgical rehabilitation?",
      answer: "Yes, Douglas Mitchell, PT, DPT specializes in ACL injury recovery and post-surgical rehabilitation. He provides comprehensive treatment plans including movement analysis, sport-specific training, and return-to-sport protocols to safely get athletes back to peak performance."
    },
    {
      question: "What is integrative dry needling and do you offer it?",
      answer: "Integrative dry needling is an advanced manual therapy technique that uses thin needles to treat trigger points and muscle dysfunction. Douglas Mitchell, PT, DPT is trained in this specialized technique and uses it as part of comprehensive treatment plans for pain relief and improved function."
    },
    {
      question: "How do I schedule an appointment at GymRatRehab?",
      answer: "You can schedule a consultation by contacting us at Info@gymratrehab.com or through our website contact form. We're available by appointment only on Tuesdays and Thursdays from 2:00 PM to 5:00 PM. We typically respond within 24 hours to appointment requests."
    },
    {
      question: "What should I expect during my first visit to GymRatRehab?",
      answer: "Your first visit includes a comprehensive evaluation of your condition, discussion of your goals, and development of a personalized treatment plan. Douglas Mitchell, PT, DPT will explain his approach to maximizing your body's healing potential and answer any questions about your recovery."
    }
  ];

  return (
    <>
      {/* Shared Banner + Pill (reduced) with title fed into Banner */}
      <Banner pillText="Frequently Asked Questions" title="Common Questions About Physical Therapy" reduced={true} />
      <section id="faq" className="pt-8 pb-20" style={{ backgroundColor: 'rgba(26, 21, 18, 0.5)' }}>
        <div className="container">
        <div className="text-center mb-8">
          {/* Removed duplicate subtitle to avoid repetition with Banner */}
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden" itemScope itemType="https://schema.org/Question">
                <button
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                  onClick={() => toggleItem(index)}
                >
                  <div className="flex items-start space-x-4">
                    <HelpCircle style={{ color: 'var(--brand-primary)' }} size={20} className="mt-1 flex-shrink-0" />
                    <h3 className="heading-3 text-left" style={{ color: 'var(--brand-primary)' }} itemProp="name">
                      {faq.question}
                    </h3>
                  </div>
                  {openItems.has(index) ? (
                    <ChevronUp style={{ color: 'var(--brand-primary)' }} size={24} className="flex-shrink-0" />
                  ) : (
                    <ChevronDown style={{ color: 'var(--brand-primary)' }} size={24} className="flex-shrink-0" />
                  )}
                </button>
                
                {openItems.has(index) && (
                  <div className="px-6 pb-6">
                    <div className="pl-10">
                      <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                        <p className="leading-relaxed" style={{ 
                          fontFamily: 'Arial, sans-serif', 
                          fontWeight: 'bold', 
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)' 
                        }} itemProp="text">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-16 p-8 rounded-2xl" style={{ background: 'linear-gradient(to right, rgba(232, 184, 109, 0.1), rgba(232, 184, 109, 0.05))', border: '1px solid rgba(232, 184, 109, 0.2)' }}>
          <h3 className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>Still Have Questions?</h3>
          <p className="body-medium mb-6 max-w-2xl mx-auto">
            Don't see your question answered? Contact Douglas Mitchell, PT, DPT directly to discuss your specific condition 
            and learn how he can help maximize your body's healing potential.
          </p>
          <button 
            className="btn-primary"
            onClick={() => {
              window.location.href = '/contact?focus=contact';
            }}
          >
            Schedule Your Consultation
          </button>
        </div>
        </div>
      </section>
    </>
  );
};